/* This example requires Tailwind CSS v2.0+ */
import { Menu, Transition } from '@headlessui/react';
import { Fragment, useEffect, useState } from 'react';

import { useMutation } from '@tanstack/react-query';
import clsx from 'clsx';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import { useAlgo } from '../hooks/useAlgo';
import { useBalanceStore } from '../hooks/useBalanceStore';
import useCheckCooldown from '../hooks/useCheckCooldown';
import { useUserAssetsQuery } from '../hooks/useUserAssetsStore';
import Alert from './Alert';
import FullScreenLoader from './FullScreenLoader';
import NftImage from './NftImage';

export default function KittySelector({ label, onSelect, index, kitty, kitties = [] }) {
  const [selectedKitty, setSelectedKitty] = useState(kitty);
  const { energyDrinkBalance } = useBalanceStore();

  kitties = kitties.sort((a, b) => (b.bio?.level ?? 1) - (a.bio?.level ?? 1));

  useEffect(() => {
    if (selectedKitty) {
      onSelect(index, selectedKitty);
    }
  }, [selectedKitty]);

  useEffect(() => {
    setSelectedKitty(kitty);
  }, [kitty]);

  return (
    <Menu as='div' className='relative text-left flex-shrink-0 w-full col-span-1 max-w-[200px]'>
      <Menu.Button
        disabled={kitties?.length === 0}
        className={`w-full text-xl font-bold rounded-lg bg-neutral-800 aspect-square ${kitty && 'pointer-events-none'}`}
      >
        {selectedKitty ? (
          <div>
            <NftImage nft={selectedKitty} alt={selectedKitty.name} className='rounded-lg' />
          </div>
        ) : (
          <div className='flex flex-col gap-0.5'>
            <p className='text-base font-normal text-white/50'>Click to select</p>
            <p>Kitty #{label}</p>
          </div>
        )}
      </Menu.Button>

      {kitties?.length === 0 && !selectedKitty && (
        <div className='absolute inset-0 w-full h-full z-20'>
          <Alert type='danger' className='w-full h-full flex items-center'>
            You don't have any Shitty Kitties that match the criteria.
          </Alert>
        </div>
      )}

      <Transition
        as={Fragment}
        enter='transition ease-out duration-100'
        enterFrom='transform opacity-0 scale-95'
        enterTo='transform opacity-100 scale-100'
        leave='transition ease-in duration-75'
        leaveFrom='transform opacity-100 scale-100'
        leaveTo='transform opacity-0 scale-95'
      >
        <Menu.Items className='absolute left-[-60px] md:left-0 z-[999] max-h-[350px] w-[325px] md:w-[375px] overflow-y-scroll  mt-2 origin-top-left rounded-lg shadow-lg bg-neutral-800 ring-1 ring-black ring-opacity-5 focus:outline-none overflow-x-hidden'>
          <div className='py-1'>
            {kitties?.map((kitty, index) => (
              <KittySelectorButton kitty={kitty} key={index} onSelect={() => setSelectedKitty(kitty)} energyDrinkBalance={energyDrinkBalance} />
            ))}
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}

const KittySelectorButton = ({ kitty, onSelect, energyDrinkBalance }) => {
  const { cooldown, loading, cooldownQuery } = useCheckCooldown(kitty.id);
  const { drinkEnergyDrink, address } = useAlgo();
  const userAssetsQuery = useUserAssetsQuery({ address });

  const drinkEnergyDrinkMutation = useMutation({
    mutationFn: drinkEnergyDrink,
    onSuccess: () => {
      userAssetsQuery.refetch();
      cooldownQuery.refetch();
      toast.success('Shitty Energy Drink consumed');
    },
    onError: (error) => {
      toast.error(error?.message || 'An error occurred');
    },
  });

  const onClick = () => {
    if (cooldown) return;
    onSelect(kitty);
  };

  const onEnergyDrink = () => {
    if (!cooldown) return;
    drinkEnergyDrinkMutation.mutate({ kitty });
  };

  const className = clsx('flex items-center w-full px-4 py-2 space-x-5 text-sm text-left text-gray-300 hover:bg-gray-900');
  const Wrapper = ({ children, onClose }) => {
    if (loading) return <></>;

    if (cooldown) {
      return <div className={className}>{children}</div>;
    }

    return (
      <button
        type='button'
        disabled={cooldown || loading}
        className={className}
        onClick={() => {
          onClick();
          onClose();
        }}
      >
        {children}
      </button>
    );
  };

  return (
    <>
      <FullScreenLoader loading={drinkEnergyDrinkMutation.isLoading} label='Drinking energy drink...' />

      <Menu.Item>
        {({ close }) => (
          <Wrapper onClose={close}>
            <div className='relative w-20 lg:w-32 h-20 lg:h-32 bg-gray-500 overflow-hidden rounded-md flex-shrink-0'>
              <NftImage nft={kitty} className={clsx('absolute inset-0 object-cover rounded-md', cooldown && 'grayscale')} />
            </div>

            <div className='flex items-center justify-between w-full gap-4'>
              <div className={clsx('flex flex-col font-medium', (cooldown || loading) && 'opacity-50')}>
                <span>{kitty?.bio?.bio?.name || kitty.name}</span>

                <div className='flex gap-3'>
                  <div className='text-sm text-neutral-500 flex flex-col'>
                    <p>Level</p> <p>{kitty?.bio?.level || 1}</p>
                  </div>
                  <div className='text-sm text-neutral-500 flex flex-col'>
                    <p>Adventures</p> <p>{kitty?.bio?.stats?.finishedAdventures || 0}</p>
                  </div>
                </div>
                {cooldown && (
                  <div className='text-sm text-neutral-500 flex flex-col'>
                    <p>Cooldown ends</p> <p>{dayjs(cooldown).format('MM/D h:mm A')}</p>
                  </div>
                )}
              </div>
              {cooldown && energyDrinkBalance > 0 && (
                <div className='flex-shrink-0'>
                  <button type='button' disabled={userAssetsQuery.isLoading} onClick={onEnergyDrink} className='relative'>
                    <div className='w-6 h-6 rounded-full bg-gray-700 absolute top-[-10px] left-[-10px] flex items-center justify-center font-medium text-sm'>
                      {energyDrinkBalance}
                    </div>
                    <img src='/shitty-energy-drink.png' alt='Energy Drink' className='md:w-14 md:h-14 w-12 h-12 rounded-md' />
                    <span className='sr-only'>Drink Energy Drink</span>
                  </button>
                </div>
              )}
            </div>
          </Wrapper>
        )}
      </Menu.Item>
    </>
  );
};
